import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export const ContactUs = () => {
  const [formData, setFormData] = useState({
    role: '',
    name: '',
    email: '',
    phone: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Contact Us | Bernard Fan Co';
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: 'c0827561-8d9e-4960-aa4d-8572f0acf851',
          subject: 'New Inquiry from Bernard Fan Co Website',
          from_name: 'Bernard Fan Co Website',
          to: 'contact@bernardfancopk.com',
          'I am a': formData.role,
          Name: formData.name,
          Email: formData.email,
          Phone: formData.phone || 'Not provided',
          City: formData.city || 'Not provided',
          Message: formData.message,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          role: '',
          name: '',
          email: '',
          phone: '',
          city: '',
          message: ''
        });
      } else {
        setError('Failed to send message. Please try again or email us directly at contact@bernardfancopk.com');
      }
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly at contact@bernardfancopk.com');
    }
    
    setIsSubmitting(false);
  };

  const openMailto = () => {
    const { role, name, email, phone, city, message } = formData;
    const mailtoBody = `I am a: ${role}%0D%0AName: ${name}%0D%0AEmail: ${email}%0D%0APhone: ${phone || 'Not provided'}%0D%0ACity: ${city || 'Not provided'}%0D%0A%0D%0AMessage:%0D%0A${encodeURIComponent(message)}`;
    const mailtoLink = `mailto:contact@bernardfancopk.com?subject=${encodeURIComponent('New Inquiry from Bernard Fan Co Website')}&body=${mailtoBody}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_fanco-preview/artifacts/saltgctp_WhatsApp%20Image%202026-01-24%20at%201.20.42%20PM%20%286%29.jpeg)' }}
        ></div>
        <div className="absolute inset-0 bg-stone-900/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white mb-6 drop-shadow-lg">
            Contact Us
          </h1>
          <p className="text-xl text-white max-w-3xl mx-auto font-light leading-relaxed drop-shadow-md">
            Have a question or want to learn more about our luxury fans? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-light tracking-tight text-stone-900 mb-6">
                  Get In Touch
                </h2>
                <p className="text-stone-600 leading-relaxed">
                  Whether you're interested in our products, need assistance with your purchase, or want to visit our showrooms, our team is here to help.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-stone-100 p-3 rounded-full">
                    <Mail className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">Email</h3>
                    <a href="mailto:contact@bernardfancopk.com" className="text-stone-600 hover:text-stone-900 transition-colors">
                      contact@bernardfancopk.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-stone-100 p-3 rounded-full">
                    <Phone className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">Phone</h3>
                    <a href="tel:+923072222302" className="text-stone-600 hover:text-stone-900 transition-colors">
                      +92 307 2222302
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-stone-100 p-3 rounded-full">
                    <MapPin className="text-stone-700" size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">Showrooms</h3>
                    <a href="/display-centres" className="text-stone-600 hover:text-stone-900 transition-colors">
                      Visit our showrooms in Karachi & Lahore
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-stone-50 p-8 rounded-lg">
              <h2 className="text-2xl font-light tracking-tight text-stone-900 mb-6">
                Send Us a Message
              </h2>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto text-green-600 mb-4" size={64} />
                  <h3 className="text-2xl font-light text-stone-900 mb-2">Thank You!</h3>
                  <p className="text-stone-600 mb-6">Your message has been sent successfully. We'll get back to you soon.</p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="bg-stone-900 hover:bg-stone-800 text-stone-50 px-8 py-3 text-sm tracking-widest transition-all duration-300"
                  >
                    SEND ANOTHER MESSAGE
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-stone-700 mb-2">
                    I am a *
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none bg-white"
                  >
                    <option value="">Select an option</option>
                    <option value="Customer">Customer</option>
                    <option value="Trader">Trader</option>
                    <option value="Interior Designer">Interior Designer</option>
                    <option value="Architect">Architect</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none"
                      placeholder="+92 300 1234567"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-stone-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none"
                      placeholder="Karachi"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-stone-500 focus:border-transparent transition-all outline-none resize-none"
                    placeholder="Tell us how we can help you..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-8 py-4 text-sm tracking-widest transition-all duration-300 disabled:opacity-50"
                >
                  <Send className="mr-2" size={18} />
                  {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                </Button>
              </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
